import { seatAwareRef } from 'utils/reservationWrites';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { Reservation, CurrentStatus } from 'types/reservations';
import firebase, { db } from 'utils/firebase';
import {
  dateHelper,
  hasBackendOwnedHold,
  timeHelper,
  timeToBlock,
} from 'utils/helper';
import logFirestoreError from 'utils/logFirestoreError';
import { Modify } from 'utils/types';
import useCurrentTime from 'CustomHooks/useCurrentTime';
import { Date as DateType, ShiftType } from 'types/shifts';
import { Shuffle, ShuffleState } from './useShuffle';
import useDateNow from './useDateNow';
import { useAddOnOrders } from 'Contexts/RestaurantContext';
import { AddOnType } from 'App/Experiences/types/addOn';
import useCollection from './useCollection';
import { Ticket } from 'types/shared/documents/restaurants/event';
import useExperimentalFlags from 'CustomHooks/useExperimentalFlags';

const PLACEHOLDER_DELETE_GRACE_MS = 10 * 1000;

export const getReservationStatus = (
  res: Reservation,
  currentTime: null | string,
) => {
  if (res.done) {
    return CurrentStatus.DONE;
  } else if (res.status === 'failed') {
    return CurrentStatus.FAILED;
  } else if (res.started) {
    if (
      res.timeStamps?.paidAt ||
      res.customData?.customStatus === CurrentStatus.PAID
    ) {
      return CurrentStatus.PAID;
    } else if (
      res.timeStamps?.lastOrderAt ||
      res.customData?.customStatus === CurrentStatus.HAS_ORDERED
    ) {
      return CurrentStatus.HAS_ORDERED;
    }

    return CurrentStatus.SEATED;
  } else if (currentTime !== null) {
    if (res.startTimeInBlocks === timeToBlock(currentTime)) {
      return CurrentStatus.UPCOMING;
    } else if (res.startTimeInBlocks < timeToBlock(currentTime)) {
      return CurrentStatus.OVERDUE;
    }
  }

  return CurrentStatus.NORMAL;
};

const useReservations = (
  restaurantId: string | null,
  date: string,
  shuffle?: ShuffleState | null,
) => {
  const { addOnOrders, getAddOnOrders } = useAddOnOrders(
    restaurantId || '',
    date,
  );

  const [tickets] = useCollection<Ticket>(`ticketsV02`, {
    filter: [
      ['restaurant', '==', restaurantId],
      ['date', '==', date],
    ],
  });

  const [data, setdata] = useState<Reservation[]>([]);
  const [loading, setloading] = useState<boolean>(true);
  const [error, seterror] = useState<null | string>(null);
  const [hideLiveResas, setHideLiveResas] = useState<boolean | undefined>();
  // The documents exactly as the listener delivered them, before the derived
  // time blocks, the length repair and a shuffle proposal: what an editor
  // compares its changes with. A ref, so it costs no extra render; a deep
  // copy, since the shown copies share nested values (tables, guest) that
  // are changed in place (e.g. sorted).
  const storedRef = useRef<Record<string, Reservation>>({});

  const currentTime = useCurrentTime(date);

  const DateNow = useDateNow();

  const autoSetTableToDoneAfterEnd =
    !!useExperimentalFlags().data?.autoSetTableToDoneAfterEnd;

  useEffect(() => {
    let active = true;
    let unsubscribe: undefined | (() => void);
    storedRef.current = {};

    if (restaurantId) {
      setloading(true);
      setdata([]);
      seterror(null);

      const ref = db
        .collection('requestsV03')
        .where('restaurant', '==', restaurantId)
        .where('date', '==', date);

      const today = dateHelper();

      unsubscribe = ref.onSnapshot(
        (querySnapshots: firebase.firestore.QuerySnapshot) => {
          if (!active) return;
          let arr: Reservation[] = [];
          let promises: Promise<void>[] = [];

          querySnapshots.forEach((doc) => {
            let data = { ...doc.data(), id: doc.id } as Reservation;

            if (data.validTill && data.validTill < DateNow()) {
              const hasPendingPaymentHold = hasBackendOwnedHold(data);

              if (data.guest?.name?.startsWith('Wird gerade')) {
                // A guest submitting just after the hold ran out still gets
                // the booking: the API renews the hold first.
                if (data.validTill < DateNow() - PLACEHOLDER_DELETE_GRACE_MS) {
                  promises.push(doc.ref.delete());
                }
              } else if (hasPendingPaymentHold) {
                // Backend cleanup owns expired payment holds.
              } else {
                promises.push(
                  seatAwareRef(restaurantId ?? '', doc.id).update({
                    validTill: 0,
                    updatedAt: DateNow(),
                    updatedBy: 'system',
                  }),
                );
              }
            } else if (date < today && !data.done && data.started) {
              promises.push(
                doc.ref.update({
                  done: true,
                  updatedAt: DateNow(),
                  updatedBy: 'system',
                }),
              );
            }

            arr.push(data);
          });

          Promise.all(promises).catch((err) => {
            // Another client (or the backend cleanup) can delete an expired
            // reservation first; Firestore then rejects the write with
            // permission-denied. Log it instead of leaving an unhandled
            // rejection.
            console.error('Reservation auto-cleanup failed', err);
          });

          storedRef.current = Object.fromEntries(arr.map((stored) => [stored.id, cloneDeep(stored)]));

          let res: Reservation[] = arr
            .sort(
              (a, b) =>
                (a?.createdAt || 1000000000) - (b?.createdAt || 1000000000),
            )
            .map((res, i) => {
              let startTimeInBlocks = timeToBlock(res.time);
              let endTimeInBlocks =
                startTimeInBlocks + (res?.reservationLength ?? 0);

              return {
                ...res,
                position: i + 1,
                startTimeInBlocks,
                endTimeInBlocks,
              };
            });

          setdata(res);
          setloading(false);
        },
        (err: Error) => {
          if (!active) return;
          logFirestoreError(
            'useReservations',
            `requestsV03 [restaurant == ${restaurantId}, date == ${date}]`,
            err,
          );
          seterror(err.message);
          setloading(false);
        },
      );

    } else {
      setdata([]);
      setloading(false);
      seterror('No Restaurant selected');
    }

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [restaurantId, date]);

  const reservations = useMemo(() => {
    let shuffleObj =
      shuffle?.reservations?.reduce(
        (acc, cV) => ({ ...acc, [cV.id]: cV }),
        {} as {
          [id: string]: {
            id: string;
            tables: string[];
            space: string;
            previousTables: string[];
          };
        },
      ) ?? {};

    const ticketIds = tickets.data?.map((t) => t.id) ?? [];

    return data.map((res) => {
      let shuffleResa = shuffleObj[res.id];

      let orders = addOnOrders.filter(
        (o) =>
          o.reservationId === res.id &&
          (o.status === 'processing' || o.status === 'succedded') &&
          !!o.cart?.length,
      );

      let addOnItems = orders.reduce((items, cV) => {
        for (const item of cV.cart) {
          if (item.type === AddOnType.SIMPLE) {
            items.push({
              id: item.id,
              line1: item.title,
              line2: '',
              price: item.total,
              refunded: !!item.refunded,
              payAtRestaurant: cV.payAtRestaurant,
            });
          } else if (item.type === AddOnType.SELECT) {
            items.push({
              id: item.id,
              line1: item.title,
              line2: `${item.choice.title}`,
              price: item.total,
              refunded: !!item.refunded,
              payAtRestaurant: cV.payAtRestaurant,
            });
          } else if (item.type === AddOnType.MULTIPLE_SELECT) {
            items.push({
              id: item.id,
              line1: item.title,
              line2: item.choices
                .filter((x) => x.count)
                .map((c) => `${c.count}x ${c.title}`)
                .join(', '),
              price: item.total,
              refunded: !!item.refunded,
              payAtRestaurant: cV.payAtRestaurant,
            });
          } else if (item.type === AddOnType.TABLE_SELECT) {
            items.push({
              id: item.id,
              line1: item.title,
              line2: '',
              price: item.total,
              refunded: !!item.refunded,
              payAtRestaurant: cV.payAtRestaurant,
            });
          }
        }

        return items;
      }, [] as { id: string; line1: string; line2: string; price: number; refunded: boolean; payAtRestaurant?: boolean }[]);

      let ticket = tickets.data[ticketIds.indexOf(res.ticketId ?? '')] ?? null;

      let ticketData: Record<string, number> = {};

      if (
        ticket &&
        (ticket.status === 'succedded' || ticket.status === 'processing')
      ) {
        for (const option of ticket.event.options ?? []) {
          ticketData['o_' + option.internalName] =
            ticket.options?.[option.id] ?? 0;
        }

        for (const option of ticket.event.optionalAddOns ?? []) {
          ticketData['a_' + option.internalName] =
            ticket.optionalAddOns?.[option.id] ?? 0;
        }
      }

      let addOnData: Record<string, number> = {};

      for (const order of orders) {
        for (const item of order.cart) {
          if (item.refunded) {
            continue;
          } else if (item.type === AddOnType.SIMPLE) {
            addOnData[item.title] = 1;
          } else if (item.type === AddOnType.SELECT) {
            addOnData[item.title + '  ' + item.choice.title] = 1;
          } else if (item.type === AddOnType.MULTIPLE_SELECT) {
            for (const choice of item.choices) {
              if (choice.count)
                addOnData[item.title + '  ' + choice.title] = choice.count;
            }
          } else if (item.type === AddOnType.TABLE_SELECT) {
            addOnData[item.title] = 1;
          }
        }
      }

      return {
        ...res,
        reservationLength:
          res.reservationLength < 0
            ? Number(res.oldReservationLength ?? 4)
            : res.reservationLength,
        currentStatus: getReservationStatus(res, currentTime),
        previousTables:
          shuffleResa?.tables &&
          JSON.stringify(shuffleResa.tables) !== JSON.stringify(res.tables)
            ? res.tables
            : null,
        tables: shuffleResa?.tables ?? res.tables,
        space: shuffleResa?.space ?? res.space,
        orders,
        addOnItems,
        name: res.guest?.name,
        ticket,
        fixed:
          res.fixed ||
          !!(
            shuffle &&
            (shuffle.fixedReservations.includes(res.id) ||
              res.fixed ||
              res.started ||
              res.done ||
              res.blocked)
          ),
        ticketData,
        addOnData,
        hasPayment:
          !!res.minimumConsumption ||
          !!res.ticketId ||
          // Refunds per item leave the order on succedded, only the items flip.
          orders.some((o) => o.cart.some((item) => !item.refunded)) ||
          !!res.hasAddOnOrders ||
          !!res.experienceVoucher,
      };
    });
  }, [data, currentTime, shuffle, hideLiveResas, addOnOrders, tickets]);

  useEffect(() => {
    if (reservations.length && date) {
      db.collection(`restaurants/${restaurantId}/reservationDates`)
        .doc(date)
        .get()
        .then((doc) => {
          if (!doc.exists) return;

          let { shifts } = doc.data() as DateType;

          shifts =
            shifts?.map((s) =>
              s.type === ShiftType.RESERVATION
                ? {
                    ...s,
                    occassions: s.occassions ?? s?.additional?.occassions ?? [],
                    spaces: s.spaces ?? s?.additional?.spaces ?? [],
                  }
                : s,
            ) ?? [];

          let shiftStatistics: Record<
            string,
            Record<string, number>
          > = shifts.reduce((acc, cV) => ({ ...acc, [cV.id]: {} }), {});

          reservations.forEach((data) => {
            if (data.status === 'failed') return;

            let shiftId = shifts.find(
              (s) =>
                s.type === ShiftType.RESERVATION &&
                (s.occassions.includes(data.occassion) ||
                  s.spaces.includes(data?.space || '')) &&
                s.start <= timeToBlock(data.time) &&
                s.close >= timeToBlock(data.time),
            )?.id;

            if (!shiftId || !shiftStatistics[shiftId]) return;

            shiftStatistics[shiftId][data.id] = data.guests;
          });

          doc.ref.update({ shiftStatistics, shifts });
        });
    }
  }, [reservations.length]);

  useEffect(() => {
    if (!autoSetTableToDoneAfterEnd) return;
    if (!restaurantId || !currentTime) return;
    if (date !== dateHelper()) return;

    const currentBlock = timeToBlock(currentTime);

    reservations.forEach((res) => {
      if (res.done || !res.started) return;
      if (!res.time) return;

      if (currentBlock < res.endTimeInBlocks) return;

      db.collection('requestsV03').doc(res.id).update({
        done: true,
        updatedAt: DateNow(),
        updatedBy: 'system',
      });
    });
  }, [
    autoSetTableToDoneAfterEnd,
    reservations,
    currentTime,
    date,
    restaurantId,
  ]);

  /** The reservation as stored (see storedRef), null when the listener has not delivered it. */
  const storedReservation = useCallback((id: string) => storedRef.current[id] ?? null, []);

  const hideLiveResasActive =
    hideLiveResas &&
    reservations.some((r) => r.currentStatus !== CurrentStatus.NORMAL);

  return {
    reservations,
    storedReservation,
    reservationError: error,
    reservationLoading: loading,
    currentTime,
    hideLiveResas:
      hideLiveResas === undefined ? hideLiveResas : hideLiveResasActive,
    setHideLiveResas,
    addOnOrders,
    getAddOnOrders,
    tickets,
  };
};

export default useReservations;
